import { Button, DatePicker, Icon, Table } from 'antd';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import { PaginationConfig } from "antd/lib/table";
import moment from 'moment';
import * as React from 'react';
import { getLogs, ILog } from "../apis/Logs";
import '../assets/css/logsTable.css';
import ColumnSearch from "./ColumnSearch";

const { RangePicker } = DatePicker;

interface ILocalProps {
    token: string,
}

interface IFilteredInfo {
    level: string[],
}

interface ILocalState {
    channelFilterDropdownVisible: boolean,
    channelFilter: string,
    customerFilterDropdownVisible: boolean,
    customerFilter: string,
    filteredInfo: IFilteredInfo | null,
    filteredLogs: ILog[],
    loading: boolean,
    logs: ILog[],
    logsOffset: number,
    timestampFilterDropdownVisible: boolean,
    timestampFilter: moment.Moment[],
}

class Logs extends React.Component<ILocalProps, ILocalState> {
    public state = {
        channelFilter: '',
        channelFilterDropdownVisible: false,
        customerFilter: '',
        customerFilterDropdownVisible: false,
        filteredInfo: null,
        filteredLogs: [],
        loading: true,
        logs: [],
        logsOffset: 1,
        timestampFilter: [],
        timestampFilterDropdownVisible: false,
    };
    public async componentDidMount() {
        const logs = await getLogs(this.props.token);
        this.setState({
            filteredLogs: logs,
            loading: false,
            logs,
            logsOffset: this.getLeastID(logs),
        });
    }

    public fetchNext = async () => {
        const logs = await getLogs(this.props.token, this.state.logsOffset);
        this.setState({
          filteredLogs: logs,
          loading: false,
          logs,
          logsOffset: logs.length > 0 ? this.getLeastID(logs) : 1,
        });
        this.onSearch('customerID', this.state.customerFilter);
    }

    public getLeastID = (logs: ILog[]) => {
        return logs[logs.length - 1].id;
    }

    // To style individual rows in a table based on level/severity
    public setRowStyles = (record: ILog) => {
        let color: string = '#FFFFFF';
        switch (record.level) {
            case 100:
                color = '#32cd325c';
                break;
            case 200:
                color = '#00dcff4a';
                break;
            case 300:
                color = '#fbff0040';
                break;
            case 400:
                color = '#ff000073';
                break;
        }
        return {
            style: { 'background': color },
        };
    };

    // Executed when the table changes
    public handleChange = (pagination: PaginationConfig, filters: any, sorter: {}) => {
        this.setState({
            filteredInfo: filters,
        });
    };

    public handleTimestampChange = (dates: RangePickerValue, dateStrings: string[]) => {
        if (dates.length === 0) {
            this.onSearch('createTimestamp', []);
        }
    }

    // Executed when we select a value from Autocomplete in column filters.
    // "!!" is used to get the truthy value of the string. It is used to identify if a filter is selected
    public onSearch = (filterType: string, searchText: any) => {
        let regex: string | RegExp = '';
        if (searchText && filterType !== 'createTimestamp') {
            regex = new RegExp(searchText, 'gi');
        }
        let stateChanges = {};
        let logs: ILog[] = [];
        if (filterType === 'createTimestamp') {
            if (searchText.length > 0) {
                const startDate: moment.Moment = searchText[0];
                const endDate: moment.Moment = searchText[1];
                logs = this.state.logs.filter((record: ILog) => moment(record.createTimestamp) >= startDate && moment(record.createTimestamp) <= endDate);
            } else {
                logs = this.state.logs;
            }
        }
        switch (filterType) {
            case 'customerID':
                logs = regex ? this.state.logs.filter((record: ILog) => `${record[filterType]}`.match(regex)) : this.state.logs;
                logs = this.state.channelFilter ? logs.filter((record: ILog) => `${record.channel}`.match(this.state.channelFilter)) : logs;
                logs = this.state.timestampFilter.length > 0 ? logs.filter((record: ILog) => moment(record.createTimestamp) >=  this.state.timestampFilter[0] && moment(record.createTimestamp) <= this.state.timestampFilter[1]) : logs;
                stateChanges = { customerFilterDropdownVisible: false, customerFilter: searchText };
                break;
            case 'channel':
                logs = this.state.customerFilter ? this.state.logs.filter((record: ILog) => `${record.customerID}`.match(this.state.customerFilter)) : this.state.logs;
                logs = regex ? logs.filter((record: ILog) => `${record[filterType]}`.match(regex)) : logs;
                logs = this.state.timestampFilter.length > 0 ? logs.filter((record: ILog) => moment(record.createTimestamp) >=  this.state.timestampFilter[0] && moment(record.createTimestamp) <= this.state.timestampFilter[1]) : logs;
                stateChanges = { channelFilterDropdownVisible: false, channelFilter: searchText };
                break;
            case 'createTimestamp':
                logs = this.state.customerFilter ? logs.filter((record: ILog) => `${record.customerID}`.match(this.state.customerFilter)) : logs;
                logs = this.state.channelFilter ? logs.filter((record: ILog) => `${record.channel}`.match(this.state.channelFilter)) : logs;
                stateChanges = { timestampFilterDropdownVisible: false, timestampFilter: searchText };
                break;
        }
        this.setState({
            ...stateChanges,
            filteredLogs: logs,
        });
    };

    public render() {
        // filteredValue is to identify if the filter is selected or not
        let filteredValue: string[] = [];
        if (this.state.filteredInfo !== null) {
            const filteredInfo: IFilteredInfo = this.state.filteredInfo!;
            filteredValue = filteredInfo.level;
        }
        // Define the columns. CustomerID, Channel, and timestamp uses custom filterting (uses Autocomplete - ColumnSearch component)
        const columns: any = [
            {
                dataIndex: 'customerID',
                filterDropdown: (
                    <div className="custom-filter-dropdown">
                        <ColumnSearch
                            data={this.state.logs}
                            onChange={this.onSearch.bind(this, 'customerID')}
                            placeholder="CustomerID Search"
                            columnName="customerID"
                        />
                    </div>
                ),
                filterDropdownVisible: this.state.customerFilterDropdownVisible,
                filterIcon: <Icon type="filter" style={{ color: this.state.customerFilter ? '#108ee9' : '#aaa' }} />,
                key: 'customerID',
                onFilterDropdownVisibleChange: (visible: boolean) => {
                    this.setState({
                        customerFilterDropdownVisible: visible,
                    });
                },
                title: 'Customer ID',
                width: '12%'
            },
            {
                dataIndex: 'channel',
                filterDropdown: (
                    <div className="custom-filter-dropdown">
                        <ColumnSearch
                            data={this.state.logs}
                            onChange={this.onSearch.bind(this, 'channel')}
                            placeholder="Channel Search"
                            columnName="channel"
                        />
                    </div>
                ),
                filterDropdownVisible: this.state.channelFilterDropdownVisible,
                filterIcon: <Icon type="filter" style={{ color: this.state.channelFilter ? '#108ee9' : '#aaa' }} />,
                key: 'channel',
                onFilterDropdownVisibleChange: (visible: boolean) => {
                    this.setState({
                        channelFilterDropdownVisible: visible,
                    });
                },
                title: 'Channel',
                width: '24%'
            },
            {
                dataIndex: 'level',
                filteredValue,
                filters: [
                    { text: '100', value: 100 },
                    { text: '200', value: 200 },
                    { text: '300', value: 300 },
                    { text: '400', value: 400 },
                ],
                key: 'level',
                onFilter: (value: string, record: ILog) => record.level === parseInt(value, 10),
                title: 'Severity',
                width: '10%',
            },
            {
                dataIndex: 'description',
                key: 'description',
                title: 'Description',
                width: '20%'
            },
            {
                dataIndex: 'payload',
                key: 'payload',
                title: 'Toolshed',
                width: '24%'
            },
            {
                dataIndex: 'createTimestamp',
                filterDropdown: (
                    <RangePicker
                        showTime={{ format: 'HH:mm:ss' }}
                        format="YYYY-MM-DD HH:mm:ss"
                        placeholder={['Start Time', 'End Time']}
                        onChange={this.handleTimestampChange}
                        onOk={this.onSearch.bind(this, 'createTimestamp')}
                    />
                ),
                filterDropdownVisible: this.state.timestampFilterDropdownVisible,
                filterIcon: <Icon type="filter" style={{ color: this.state.timestampFilter.length > 0 ? '#108ee9' : '#aaa' }} />,
                key: 'createTimestamp',
                onFilterDropdownVisibleChange: (visible: boolean) => {
                    this.setState({
                        timestampFilterDropdownVisible: visible,
                    });
                },
                title: 'Timestamp',
                width: '10%'
            },
        ];
        return (
            <div>
                <Table
                    dataSource={this.state.filteredLogs}
                    columns={columns}
                    loading={this.state.loading}
                    rowKey='id'
                    className="preserveSpace"
                    onRow={this.setRowStyles}
                    bordered={true}
                    onChange={this.handleChange}
                />
                <Button style={{ float: 'right', marginTop: 2 }} onClick={this.fetchNext} type="primary">Next</Button>
            </div>
        );
    }
}

export default Logs;
